/**
 * UI-side lifecycle telemetry — minimal, console-based.
 *
 * Goal: produce a client-side trail that lets us correlate a user-reported
 * issue ("I clicked Create and nothing happened") forward to the API trace
 * via the operation_id sent on the X-Operation-Id header.
 *
 * This is intentionally lightweight — no browser OTel SDK, no analytics
 * vendor. Events go to `console.info` / `console.warn` so they are visible in
 * DevTools and can be surfaced by any error-tracking layer (e.g. Sentry
 * breadcrumbs) the app installs later without changing call sites.
 */

export type LifecycleAction = 'create' | 'update' | 'suspend' | 'resume' | 'delete' | 'refresh';

export type LifecycleOutcome = 'started' | 'success' | 'error';

export type LifecycleResource =
  | 'query-service'
  | 'laminar'
  | 'metadata-services'
  | 'warmpool'
  | 'catalog'
  | 'access-token'
  | 'service-account'
  | 'governance'
  | 'role'
  | 'role-binding'
  | 'group';

export interface LifecycleEvent {
  resource: LifecycleResource;
  action: LifecycleAction;
  operationId: string;
  outcome: LifecycleOutcome;
  resourceName?: string;
  error?: string;
  extra?: Record<string, unknown>;
}

export interface ToastEvent {
  variant: 'success' | 'error' | 'warning' | 'info' | 'destructive';
  title?: string;
  description?: string;
  operationId?: string;
  source?: string;
}

/**
 * Generate a new operation_id for a single logical user action.
 *
 * The format is 32 lowercase hex characters, which is exactly a W3C trace id.
 * It used to be a hyphenated UUID, which carries the same 128 bits but is not
 * accepted by Tempo's trace-id search — so the id in the logs and the id in
 * Tempo were the same value written two ways, and people had to strip the
 * dashes by hand. Emitting hex makes one string work in both.
 *
 * Every branch must therefore produce hex. The old fallbacks emitted base36
 * with a hyphen, which `traceparentFor` below correctly refuses, so those
 * sessions silently sent no traceparent at all.
 *
 * Not a security-sensitive value: it only needs to be distinct enough to
 * correlate one user action, not to resist prediction.
 */
let fallbackCounter = 0;

export function newOperationId(): string {
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.getRandomValues === 'function') {
      const id = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
        b.toString(16).padStart(2, '0')
      ).join('');
      // The all-zero id is the one value the spec forbids. Astronomically
      // unlikely, but the failure is silent, so it is worth the one check.
      if (!/^0+$/.test(id)) {
        return id;
      }
    }
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '');
    }
  }
  // No Web Crypto at all: time plus a counter, padded to a full-width id.
  // Weak entropy, but still a well-formed trace id rather than a broken one.
  fallbackCounter += 1;
  const seed = `${Date.now().toString(16)}${fallbackCounter.toString(16).padStart(4, '0')}`;
  return seed.padStart(32, '0').slice(-32);
}

/**
 * Build a W3C `traceparent` header whose trace id IS the operation id.
 *
 * Why the UI does this at all: Envoy sits in front of compute-api with OTel
 * tracing on, and a tracer only generates a trace id when the request does not
 * already carry one. Today nothing sends `traceparent`, so Envoy invents an id
 * and propagates it, and compute-api's span becomes a child of Envoy's trace
 * instead of the operation's — the API half of a cluster create ends up in a
 * different trace from the operator half.
 *
 * Sending the header closes that: Envoy adopts this trace, passes it to
 * compute-api, and the operator independently derives the same id from the
 * e6.io/operation-id annotation. One trace across all four.
 *
 * Returns null when the operation id is not a UUID. newOperationId() falls back
 * to a timestamp-based id when the Web Crypto API is missing, and those are not
 * 128-bit values. A malformed traceparent is silently ignored by Envoy, which
 * looks identical to sending nothing — so we send nothing deliberately rather
 * than something broken, and Envoy generates as it does today.
 */
export function traceparentFor(operationId: string): string | null {
  const traceId = operationId.replace(/-/g, '').toLowerCase();

  // 16 bytes as hex, and not the all-zero id, which the spec declares invalid.
  if (!/^[0-9a-f]{32}$/.test(traceId) || /^0{32}$/.test(traceId)) {
    return null;
  }

  const spanId = newSpanId();
  if (!spanId) return null;

  // version 00, trace id, span id, flags 01 = sampled.
  return `00-${traceId}-${spanId}-01`;
}

/**
 * 8 random bytes as hex, for the span-id field of a traceparent.
 *
 * Returns null when no Web Crypto API is available: a span id must be random,
 * and there is no acceptable fallback — an all-zero or predictable span id
 * invalidates the whole header.
 */
function newSpanId(): string | null {
  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
    return null;
  }
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return /^0{16}$/.test(hex) ? null : hex;
}

/**
 * Log a lifecycle event to the browser console with a structured payload.
 * Use the same key `lifecycle` across all calls so DevTools can filter.
 */
export function logLifecycle(event: LifecycleEvent): void {
  const payload = {
    ...event,
    timestamp: new Date().toISOString(),
  };

  if (event.outcome === 'error') {
    console.warn('lifecycle', payload);
  } else {
    console.info('lifecycle', payload);
  }
}

/**
 * Log a user-visible toast / banner event.
 */
export function logToast(event: ToastEvent): void {
  const payload = {
    ...event,
    timestamp: new Date().toISOString(),
  };

  if (event.variant === 'error' || event.variant === 'destructive') {
    console.warn('toast.shown', payload);
  } else {
    console.info('toast.shown', payload);
  }
}

/**
 * Wrap a mutation function with full lifecycle telemetry — emits `started`
 * before the call, `success` after a successful resolve, `error` (with the
 * error message) on rejection.
 */
export async function tracked<T>(
  event: Omit<LifecycleEvent, 'outcome' | 'operationId'>,
  fn: (operationId: string) => Promise<T>
): Promise<T> {
  const operationId = newOperationId();
  logLifecycle({ ...event, operationId, outcome: 'started' });
  try {
    const result = await fn(operationId);
    logLifecycle({ ...event, operationId, outcome: 'success' });
    return result;
  } catch (err) {
    logLifecycle({
      ...event,
      operationId,
      outcome: 'error',
      error: err instanceof Error ? err.message : String(err),
    });
    throw err;
  }
}
